'use client';

import { useEffect, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card } from '@/components/Card';
import { Input } from '@/components/Input';
import { NeedsAuth } from '@/components/NeedsAuth';
import { PageTitle } from '@/components/PageTitle';
import { getClient } from '@/lib/api';
import { eur } from '@/lib/fmt';
import type { components } from '@/lib/api.types';

type Summary = components['schemas']['AnalyticsSummary'];

const PALETTE = [
  '#14b8a6', '#0ea5e9', '#a855f7', '#f59e0b', '#ef4444',
  '#22c55e', '#ec4899', '#6366f1', '#84cc16', '#eab308',
];

export default function CategoriesPage() {
  return (
    <NeedsAuth>
      <Inner />
    </NeedsAuth>
  );
}

function Inner() {
  const [days, setDays] = useState(30);
  const [parentOnly, setParentOnly] = useState(true);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const client = getClient();
    if (!client) return;
    void (async () => {
      if (!cancelled) setError(null);
      const { data, error } = await client.GET('/api/analytics/summary', {
        params: { query: { days, parent_only: parentOnly, top: 20 } },
      });
      if (cancelled) return;
      if (error) setError(JSON.stringify(error));
      else setSummary(data ?? null);
    })();
    return () => {
      cancelled = true;
    };
  }, [days, parentOnly]);

  const pieData = useMemo(
    () =>
      (summary?.by_category ?? []).map((c, i) => ({
        name: c.category,
        value: Number(c.total),
        fill: PALETTE[i % PALETTE.length],
      })),
    [summary],
  );

  const barData = useMemo(
    () =>
      (summary?.top_merchants ?? []).slice(0, 10).map((m) => ({
        name: m.label.length > 24 ? m.label.slice(0, 24) + '…' : m.label,
        total: Number(m.total),
      })),
    [summary],
  );

  return (
    <div className="flex flex-col gap-6">
      <PageTitle
        actions={
          <div className="flex items-center gap-3 text-sm">
            <label className="flex items-center gap-2 text-fg-muted">
              Days
              <Input
                type="number"
                min={1}
                max={365}
                value={days}
                onChange={(e) => setDays(Number(e.target.value) || 30)}
                className="!w-20 !py-1"
              />
            </label>
            <label className="flex items-center gap-2 text-fg-muted">
              <input
                type="checkbox"
                checked={parentOnly}
                onChange={(e) => setParentOnly(e.target.checked)}
                className="accent-[var(--hero-start)]"
              />
              Parent only
            </label>
          </div>
        }
      >
        Categories
      </PageTitle>

      {error && (
        <Card>
          <p className="text-sm text-negative">Error: {error}</p>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <Card title={`Spending by category — last ${days}d`}>
          {pieData.length === 0 ? (
            <p className="py-12 text-center text-sm text-fg-muted">
              No spending in this window.
            </p>
          ) : (
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={60}
                    outerRadius={110}
                    paddingAngle={1}
                  >
                    {pieData.map((entry, i) => (
                      <Cell key={i} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v: number) => eur(v)} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>

        <Card title={`Top merchants — last ${days}d`}>
          {barData.length === 0 ? (
            <p className="py-12 text-center text-sm text-fg-muted">
              No spending in this window.
            </p>
          ) : (
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={barData} layout="vertical" margin={{ left: 10, right: 20 }}>
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={140} />
                  <Tooltip formatter={(v: number) => eur(v)} />
                  <Bar dataKey="total" fill="var(--hero-start)" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>
      </div>

      {summary && summary.by_category.length > 0 && (
        <Card title="Detail">
          <table className="w-full text-sm">
            <thead className="text-left text-fg-muted">
              <tr>
                <th className="py-2 font-medium">Category</th>
                <th className="py-2 text-right font-medium">Transactions</th>
                <th className="py-2 text-right font-medium">Total</th>
              </tr>
            </thead>
            <tbody>
              {summary.by_category.map((c) => (
                <tr key={c.category} className="border-t border-hairline">
                  <td className="py-2 text-fg-primary">{c.category}</td>
                  <td className="tnum py-2 text-right">{c.transaction_count}</td>
                  <td className="tnum py-2 text-right font-mono">{eur(c.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
